import {
    Compass,
    Search,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";
import {
    useMemo,
} from "preact/compat";

import type {
    BrowsingMode,
} from "../../constants.ts";
import {
    useViewerStore,
} from "../../stores/viewerStore.ts";
import type {
    LeftTab,
} from "../../stores/viewerStore.ts";
import {
    BrowsePanel,
} from "./BrowsePanel.tsx";
import {
    SearchPanel,
} from "./SearchPanel.tsx";

type ViewerLeftSidebarProps = {
    open: boolean;
    onApplyFilters: () => void;
    onBrowsingModeChange: (mode: BrowsingMode) => void;
    onSearch: () => void;
    onClearSearchAndBrowse: () => void;
    onTagStatsClick: (tagOrQuery: string) => void;
    onFetchExactCount: () => void;
};

type TabConfig = {
    id: LeftTab;
    label: string;
    icon: ReactElement;
};

const TABS: TabConfig[] = [
    {
        id: "browse",
        label: "Browse",
        icon: <Compass size={16} />,
    },
    {
        id: "search",
        label: "Search",
        icon: <Search size={16} />,
    },
];

export const ViewerLeftSidebar = ({
    open,
    onApplyFilters,
    onBrowsingModeChange,
    onSearch,
    onClearSearchAndBrowse,
    onTagStatsClick,
    onFetchExactCount,
}: ViewerLeftSidebarProps): ReactElement => {
    const {
        activeLeftTab,
        setActiveLeftTab,
        filters,
        isSearchActive,
        searchTotalCount,
    } = useViewerStore();

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.media_type && filters.media_type !== "all") count++;
        if (filters.has_watermark) count++;
        if (filters.tag_query?.trim()) count++;
        return count;
    }, [filters]);

    const getTabBadge = (tabId: LeftTab): number | null => {
        if (tabId === "browse" && activeFilterCount > 0) {
            return activeFilterCount;
        }
        if (tabId === "search" && isSearchActive && searchTotalCount > 0) {
            return searchTotalCount;
        }
        return null;
    };

    return (
        <aside
            className={`
                fixed left-0 top-14 bottom-0 w-72 bg-bg-secondary border-r border-border
                transform transition-transform duration-300 z-20 flex flex-col
                ${open ? "translate-x-0" : "-translate-x-full"}
            `}
        >
            {/* tab bar */}
            <div className="flex border-b border-border shrink-0">
                {TABS.map((tab) => {
                    const isActive = activeLeftTab === tab.id;
                    const badge = getTabBadge(tab.id);
                    return (
                        <button
                            type="button"
                            key={tab.id}
                            onClick={() => setActiveLeftTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors relative ${
                                isActive
                                    ? "text-accent"
                                    : "text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                            {badge !== null && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-accent text-white rounded-full leading-none">
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            )}
                            {isActive && (
                                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-t" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* tab content */}
            <div className="flex-1 overflow-y-auto p-4">
                {activeLeftTab === "browse" && (
                    <BrowsePanel
                        onApplyFilters={onApplyFilters}
                        onBrowsingModeChange={onBrowsingModeChange}
                        onFetchExactCount={onFetchExactCount}
                        onTagStatsClick={onTagStatsClick}
                    />
                )}
                {activeLeftTab === "search" && (
                    <SearchPanel
                        onSearch={onSearch}
                        onClearSearchAndBrowse={onClearSearchAndBrowse}
                    />
                )}
            </div>
        </aside>
    );
};
